export type CompositionAgentOp =
    | 'ping'
    | 'seek'
    | 'rasterize'
    | 'serialize'
    | 'timelineInfo'
    | 'setNodeText'
    | 'setNodeStyle';

export interface CompositionAgentRequest {
    l5: 1;
    token: string;
    id: number;
    op: CompositionAgentOp;
    args?: Record<string, unknown>;
}

export interface CompositionAgentResponse {
    l5: 1;
    token: string;
    id: number;
    ok: boolean;
    result?: unknown;
    error?: string;
}

export interface CompositionSerialization {
    html: string;
    /** Elements in the serialized copy, for the fidelity check. Counted on what `html` was built from. */
    elementCount: number;
    /** Elements carrying a shadow root, whose content no serialization of the copy can preserve. */
    shadowHosts: number;
}

/**
 * One tween as the composition's timeline schedules it. A `set` is a tween whose `end` is its
 * `start`.
 */
export interface CompositionTweenFact {
    /** Seconds on the main timeline. */
    start: number;
    end: number;
    /** The properties it writes, as the composition named them (`autoAlpha`, `x`, `--d`). */
    properties: string[];
    /**
     * Its element targets, each an index into the introspection document's
     * `body.querySelectorAll('*')`. Targets that are not elements are left out.
     */
    targets: number[];
}

function createCompositionAgent(token: string, hostOrigin: string): void {
    const listen = window.addEventListener.bind(window);
    const stringify = JSON.stringify;
    const host = window.parent;

    const post = (message: Record<string, unknown>, transfer: Transferable[] = []): void => {
        host.postMessage(message, hostOrigin, transfer);
    };

    interface SeekableTimeline {
        progress(value: number, suppressEvents?: boolean): unknown;
        totalTime?(value: number, suppressEvents?: boolean): unknown;
    }

    function timeline(): SeekableTimeline | null {
        const registry = (window as unknown as { __timelines?: { main?: SeekableTimeline } })
            .__timelines;
        return registry?.main ?? null;
    }

    function seek(args: Record<string, unknown>): null {
        const target = timeline();
        if (!target) {
            return null;
        }
        const progress = Number(args.progress) || 0;
        const clamped = Math.max(0, Math.min(1, progress));
        try {
            if (args.mode === 'totalTime' && typeof target.totalTime === 'function') {
                target.totalTime(Number(args.seconds) || 0, false);
            } else {
                target.progress(clamped, false);
            }
        } catch {}
        return null;
    }

    async function rasterize(args: Record<string, unknown>): Promise<ImageBitmap> {
        const snap = (
            window as unknown as {
                snapdom?: { toCanvas(node: Element, options: unknown): Promise<HTMLCanvasElement> };
            }
        ).snapdom;
        if (!snap) {
            throw new Error('snapdom is not available in the composition frame');
        }
        const canvas = await snap.toCanvas(document.documentElement, {
            backgroundColor: 'transparent',
            clip: 'viewport',
            width: Number(args.width) || 0,
            height: Number(args.height) || 0,
            dpr: Number(args.scale) || 1,
        });
        return createImageBitmap(canvas);
    }

    function serialize(): CompositionSerialization {
        // Scripts are dropped from the copy rather than carried into it: the inert frame cannot run
        // them, and the inlined snapDOM source alone is half the serialized payload — re-sent over
        // the port and re-parsed at every pose a sampling rule takes.
        const clone = document.documentElement.cloneNode(true) as HTMLElement;
        const scripts = clone.querySelectorAll('script');
        for (let i = 0; i < scripts.length; i++) {
            scripts[i].remove();
        }
        return {
            // Counted on the clone, which is what the copy is parsed from, so the fidelity check
            // compares like with like instead of tripping over the scripts removed here. Descendants
            // of the root element, not of the document — the check counts the copy the same way.
            html: `<!doctype html>${clone.outerHTML}`,
            elementCount: clone.getElementsByTagName('*').length,
            shadowHosts: shadowHostCount(),
        };
    }

    // Shadow content survives neither `outerHTML` nor `getElementsByTagName`, so it cannot be caught
    // by comparing element counts — the host is counted identically on both sides. Report it
    // directly instead, so the copy is rejected rather than silently measured without it.
    function shadowHostCount(): number {
        let hosts = 0;
        const all = document.getElementsByTagName('*');
        for (let i = 0; i < all.length; i++) {
            if ((all[i] as HTMLElement).shadowRoot) {
                hosts += 1;
            }
        }
        return hosts;
    }

    function timelineInfo(): {
        time: number;
        duration: number;
        measurable: boolean;
        tweens: CompositionTweenFact[];
    } | null {
        const target = timeline() as
            | (SeekableTimeline & { time?(): number; duration?(): number })
            | null;
        if (!target) {
            return null;
        }
        if (typeof target.time !== 'function') {
            return { time: 0, duration: 0, measurable: false, tweens: [] };
        }
        try {
            return {
                time: target.time(),
                duration: typeof target.duration === 'function' ? target.duration() : 0,
                measurable: true,
                tweens: readableTweenFacts(target),
            };
        } catch {
            return { time: 0, duration: 0, measurable: false, tweens: [] };
        }
    }

    interface ScheduledAnimation {
        startTime(): number;
        duration(): number;
        totalDuration(): number;
        timeScale(): number;
        parent?: ScheduledAnimation | null;
        targets?(): unknown[];
        vars?: Record<string, unknown>;
        timeline?: {
            getChildren?(
                nested: boolean,
                tweens: boolean,
                timelines: boolean,
            ): ScheduledAnimation[];
        } | null;
    }

    // The keys of a tween's vars that configure it rather than name a property it writes.
    const TWEEN_CONFIG_KEYS = new Set([
        'autoRevert',
        'callbackScope',
        'data',
        'defaults',
        'delay',
        'duration',
        'ease',
        'easeEach',
        'id',
        'immediateRender',
        'inherit',
        'keyframes',
        'lazy',
        'modifiers',
        'onComplete',
        'onCompleteParams',
        'onInterrupt',
        'onInterruptParams',
        'onRepeat',
        'onRepeatParams',
        'onReverseComplete',
        'onReverseCompleteParams',
        'onStart',
        'onStartParams',
        'onUpdate',
        'onUpdateParams',
        'overwrite',
        'parent',
        'paused',
        'repeat',
        'repeatDelay',
        'repeatRefresh',
        'reversed',
        'runBackwards',
        'snap',
        'stagger',
        'startAt',
        'yoyo',
        'yoyoEase',
    ]);
    const MAX_REPORTED_TWEENS = 2000;

    const PERCENT_KEYFRAME = /^\d+(\.\d+)?%$/;

    function ownProperties(vars: Record<string, unknown>): string[] {
        return Object.keys(vars).filter((key) => !TWEEN_CONFIG_KEYS.has(key));
    }

    // A keyframes tween writes nothing of its own: its properties are in its steps, given as an
    // array of steps, as lists of values per property, or as steps keyed by percentage.
    function writtenProperties(vars: Record<string, unknown>): string[] {
        const written = new Set(ownProperties(vars));
        const keyframes = vars.keyframes;
        const steps: unknown[] = Array.isArray(keyframes)
            ? keyframes
            : keyframes && typeof keyframes === 'object'
              ? [keyframes]
              : [];
        for (const step of steps) {
            if (!step || typeof step !== 'object') {
                continue;
            }
            for (const key of ownProperties(step as Record<string, unknown>)) {
                const nested = (step as Record<string, unknown>)[key];
                if (PERCENT_KEYFRAME.test(key) && nested && typeof nested === 'object') {
                    ownProperties(nested as Record<string, unknown>).forEach((name) =>
                        written.add(name),
                    );
                } else {
                    written.add(key);
                }
            }
        }
        return Array.from(written);
    }

    function variesPerTarget(value: unknown): boolean {
        return typeof value === 'function' || typeof value === 'string';
    }

    // GSAP runs a staggered tween, or one whose duration or delay varies per target, as a tween
    // per target on a timeline of its own, so each target is written only over its own span. Left
    // whole when it repeats, since every repeat writes each target again.
    function perTargetTweens(tween: ScheduledAnimation): ScheduledAnimation[] {
        const vars = tween.vars ?? {};
        const own = tween.timeline;
        const splits =
            Boolean(vars.stagger) || variesPerTarget(vars.duration) || variesPerTarget(vars.delay);
        if (
            !splits ||
            !own ||
            typeof own.getChildren !== 'function' ||
            tween.totalDuration() !== tween.duration()
        ) {
            return [tween];
        }
        return own.getChildren(false, true, false);
    }

    // Indexed as the introspection copy will index them: that copy drops every script.
    function elementIndex(): Map<Element, number> {
        const index = new Map<Element, number>();
        const all = document.body ? document.body.querySelectorAll('*') : [];
        for (let i = 0; i < all.length; i++) {
            if (all[i].tagName !== 'SCRIPT') {
                index.set(all[i], index.size);
            }
        }
        return index;
    }

    // A timeline whose tweens cannot be read still has a clock the reading-time rules depend on.
    function readableTweenFacts(main: SeekableTimeline): CompositionTweenFact[] {
        try {
            return tweenFacts(main);
        } catch {
            return [];
        }
    }

    function tweenFacts(main: SeekableTimeline): CompositionTweenFact[] {
        const nested = main as SeekableTimeline & {
            getChildren?(
                nested: boolean,
                tweens: boolean,
                timelines: boolean,
            ): ScheduledAnimation[];
        };
        if (typeof nested.getChildren !== 'function') {
            return [];
        }
        const elements = elementIndex();
        return nested
            .getChildren(true, true, false)
            .slice(0, MAX_REPORTED_TWEENS)
            .flatMap(perTargetTweens)
            .slice(0, MAX_REPORTED_TWEENS)
            .map((tween) => {
                let start = tween.startTime();
                let length = tween.totalDuration() / (tween.timeScale() || 1);
                for (
                    let parent = tween.parent;
                    parent && parent !== (nested as unknown);
                    parent = parent.parent
                ) {
                    const scale = parent.timeScale() || 1;
                    start = parent.startTime() + start / scale;
                    length = length / scale;
                }
                const targets = typeof tween.targets === 'function' ? tween.targets() : [];
                return {
                    start,
                    end: start + length,
                    properties: writtenProperties(tween.vars ?? {}),
                    targets: targets
                        .map((target) =>
                            target instanceof Element ? elements.get(target) : undefined,
                        )
                        .filter((index): index is number => index !== undefined),
                };
            });
    }

    function setNodeText(args: Record<string, unknown>): null {
        const node = document.getElementById(String(args.id));
        if (node) {
            node.innerHTML = String(args.html);
        }
        return null;
    }

    function setNodeStyle(args: Record<string, unknown>): null {
        const node = document.getElementById(String(args.id));
        const styles = (args.styles ?? {}) as Record<string, string>;
        if (node) {
            for (const property of Object.keys(styles)) {
                node.style.setProperty(property, styles[property]);
            }
        }
        return null;
    }

    async function run(op: string, args: Record<string, unknown>): Promise<unknown> {
        if (op === 'ping') return true;
        if (op === 'seek') return seek(args);
        if (op === 'rasterize') return rasterize(args);
        if (op === 'serialize') return serialize();
        if (op === 'timelineInfo') return timelineInfo();
        if (op === 'setNodeText') return setNodeText(args);
        if (op === 'setNodeStyle') return setNodeStyle(args);
        throw new Error(`unknown op ${stringify(op)}`);
    }

    function handle(data: { id?: number; op?: string; args?: Record<string, unknown> }): void {
        if (typeof data.id !== 'number') {
            return;
        }
        const id = data.id;
        const answer = async (): Promise<void> => {
            try {
                const result = await run(String(data.op), data.args ?? {});
                const transfer = result instanceof ImageBitmap ? [result] : [];
                post({ l5: 1, token, id, ok: true, result }, transfer);
            } catch (error: unknown) {
                post({
                    l5: 1,
                    token,
                    id,
                    ok: false,
                    error: String((error as Error)?.message ?? error),
                });
            }
        };
        void answer();
    }

    listen('message', (event: MessageEvent) => {
        const data = event.data as { l5?: number; token?: string; op?: string } | null;
        if (!data || data.l5 !== 1 || data.token !== token || event.source !== host) {
            return;
        }
        if (typeof data.op === 'string') {
            handle(data);
        }
    });

    function postReady(): void {
        post({ l5: 1, token, event: 'ready' });
    }

    if (document.readyState === 'complete') {
        postReady();
    } else {
        listen('load', postReady);
    }
}

export function compositionAgentSource(token: string, hostOrigin: string): string {
    return `(${createCompositionAgent.toString()})(${JSON.stringify(token)}, ${JSON.stringify(hostOrigin)});`;
}
